import styled from "@emotion/styled";
import InnoIcons from "@InnoIcons/";
import theme from "@theme/";

// Main container
const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

// Dropdown button
const DropdownButton = styled.button`
  background: white;
  border: 1px solid #e5e7eb;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  // min-width: 180px;
  min-width: ${(props) => props.minWidth || "180px"};

  justify-content: space-between;
  transition: all 0.2s ease;

  /* subtle shadow by default */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15); /* slightly stronger on hover */
  }

  /* Open state */
  ${(props) =>
    props.isOpen &&
    `
    border-color: ${theme.colors.themeColor};
    box-shadow: 0 0 0 2px ${theme.colors.themeColor}, 0 4px 12px rgba(0,0,0,0.15);
  `}

  /* Active state (filter applied) */
  ${(props) =>
    props.isActive &&
    `
    border-color: ${theme.colors.themeColor}; 
    background-color:${theme.colors.buttonbackgroundfilter}; /* subtle light orange */
    color: ${theme.colors.themeColor};
  `}
`;

// Dropdown menu
const DropdownMenu = styled.div`
  padding: 8px 12px;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  margin-top: 4px;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  // min-width: 220px;
  min-width: ${(props) => props.minWidth || "220px"};

  min-height: 200px;
`;

// Dropdown header
const DropdownHeader = styled.div`
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f8f9fa;
  font-size: 12px;
  color: #111827;
  font-weight: 500;
  cursor: ${(props) => (props.isClickable ? "pointer" : "default")};
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${(props) => (props.isClickable ? "#f1f3f4" : "#f8f9fa")};
  }
`;

const HeaderText = styled.span`
  flex: 1;
  font-size: 12px;
  color: #111827;
`;

const HeaderChevron = styled(InnoIcons.KeyboardArrowRightIcon)`
  width: 20px;
  height: 20px;
  color: ${theme.colors.themeColor};
  transition: transform 0.2s ease;
  transform: ${(props) =>
    props.isExpanded ? "rotate(90deg)" : "rotate(0deg)"};
`;

const HeaderOptionsContainer = styled.div`
  display: ${(props) => (props.isVisible ? "block" : "none")};
  position: absolute;
  top: 15%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 150px;
  margin: 0px 10px;
`;

const HeaderOption = styled.div`
  padding: 8px 16px;
  font-size: 12px;
  cursor: pointer;
  background: ${(props) =>
    props.isSelected ? theme.gradient.backgroundTheme : "transparent"};
  color: ${(props) => (props.isSelected ? theme.colors.themeColor : "#374151")};
  transition: background-color 0.2s ease;
  position: relative;
  z-index: auto;
  &:hover {
    background: ${(props) =>
      props.isSelected ? theme.gradient.backgroundTheme : "#f9fafb"};
  }
`;

// Footer
const DropdownFooter = styled.div`
  padding: 16px 0px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const UpdateButton = styled.button`
  background: #d97706;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #b45309;
  }
`;

// Clear / Update buttons
const ClearAllButton = styled.p`
  background: ${(props) => (props.primary ? "#EC810B" : "transparent")};
  color: ${(props) => (props.primary ? "#fff" : "#6b7280")};
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  padding-block: 0px;
  margin-block: 0px;

  &:hover {
    background-color: ${(props) => (props.primary ? "#d97706" : "#f3f4f6")};
    color: ${(props) => (props.primary ? "#fff" : "#374151")};
  }
`;

const UnitSelect = styled.select`
  height: 34px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  padding: 0 12px;
  font-size: 14px;
  color: #374151;
  margin-left: 8px;
  background: white;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: #f97316;
    box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.2);
  }
`;

const ChevronIcon = styled(InnoIcons.KeyboardArrowDownIcon)`
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  color: ${(props) =>
    props.isActive ? `${theme.colors.themeColor}` : "black"};
`;

const SC = {
  DropdownContainer,
  DropdownButton,
  DropdownMenu,
  DropdownHeader,
  HeaderText,
  HeaderChevron,
  HeaderOptionsContainer,
  HeaderOption,
  DropdownFooter,
  ClearAllButton,
  UnitSelect,
  UpdateButton,
  ChevronIcon,
};

export default SC;
